import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export type KycStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'KEEP_IN_VIEW' | string;

interface KycActionsPanelProps {
    driverId: number;
    currentStatus: KycStatus;
    onApprove: (driverId: number) => Promise<void> | void;
    onReject: (driverId: number, reason: string) => Promise<void> | void;
    onKeepInView: (driverId: number) => Promise<void> | void;
    disabled?: boolean;
}

const KycActionsPanel: React.FC<KycActionsPanelProps> = ({
    driverId,
    currentStatus,
    onApprove,
    onReject,
    onKeepInView,
    disabled = false,
}) => {
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [keepInViewDialogOpen, setKeepInViewDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const statusUpper = currentStatus?.toUpperCase() || 'PENDING';
    const isApproved = statusUpper === 'APPROVED';
    const isRejected = statusUpper === 'REJECTED';

    const handleApprove = async () => {
        setIsLoading(true);
        try {
            await onApprove(driverId);
            toast.success('KYC documents approved successfully');
            setApproveDialogOpen(false);
        } catch (error) {
            toast.error('Failed to approve KYC documents');
        } finally {
            setIsLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        setIsLoading(true);
        try {
            await onReject(driverId, rejectionReason);
            toast.success('KYC documents rejected');
            setRejectDialogOpen(false);
            setRejectionReason('');
        } catch (error) {
            toast.error('Failed to reject KYC documents');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeepInView = async () => {
        setIsLoading(true);
        try {
            await onKeepInView(driverId);
            toast.success('KYC documents marked for review');
            setKeepInViewDialogOpen(false);
        } catch (error) {
            toast.error('Failed to update KYC status');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>KYC Actions</CardTitle>
                    <CardDescription>
                        Review and update the KYC verification status for this driver
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={() => setApproveDialogOpen(true)}
                            disabled={disabled || isApproved}
                            className="bg-success hover:bg-success/90 text-success-foreground"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve KYC
                        </Button>

                        <Button
                            onClick={() => setRejectDialogOpen(true)}
                            disabled={disabled || isRejected || isApproved}
                            variant="destructive"
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject KYC
                        </Button>

                        <Button
                            onClick={() => setKeepInViewDialogOpen(true)}
                            disabled={disabled || isApproved || isRejected}
                            variant="outline"
                            className="border-blue-500 text-blue-500 hover:bg-blue-50"
                        >
                            <Eye className="w-4 h-4 mr-2" />
                            Keep in View
                        </Button>
                    </div>

                    {isApproved && (
                        <p className="text-sm text-success mt-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            KYC documents are already approved. All actions have been disabled.
                        </p>
                    )}

                    {isRejected && (
                        <p className="text-sm text-destructive mt-3 flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            KYC documents have been rejected
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Approve Confirmation Dialog */}
            <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Approve KYC Documents?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will approve the driver's KYC documents and allow them to start accepting rides.
                            Make sure you have reviewed all documents carefully.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleApprove}
                            disabled={isLoading}
                            className="bg-success hover:bg-success/90"
                        >
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Approve
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reject Dialog with Reason */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reject KYC Documents</DialogTitle>
                        <DialogDescription>
                            Please provide a reason for rejecting the KYC documents. This will be shared with the
                            driver.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="rejectionReason">
                                Rejection Reason <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="rejectionReason"
                                placeholder="e.g., Document is blurry, ID has expired, etc."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                rows={4}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleReject} disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Reject KYC
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Keep in View Confirmation Dialog */}
            <AlertDialog open={keepInViewDialogOpen} onOpenChange={setKeepInViewDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Mark for Review?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will mark the KYC documents as "Keep in View" for further review. The driver will
                            not be able to accept rides until the documents are approved.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleKeepInView}
                            disabled={isLoading}
                            className="bg-blue-500 hover:bg-blue-600"
                        >
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Keep in View
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default KycActionsPanel;
